/**
 * MKS-294 — the lead board's staleness clock, in one place.
 *
 * Staleness used to be dated from `stageChangedAt`, so a lead that is being
 * worked every week — edits, comments, role changes — still went red for as
 * long as it stayed in one column, and people stopped trusting the warning.
 * The backend now returns `lastActivityAt` (max of stage change, lead edit,
 * note and role change; see `leadLastActivityAt` in `leadService.ts`), and
 * every surface that marks a stale lead reads it through `leadActivityAt`
 * below rather than picking a timestamp of its own.
 */
import type { Lead } from '../../services/api/leads';

type LeadActivityFields = Pick<Lead, 'stageChangedAt' | 'lastActivityAt'>;

/**
 * Which timestamp does staleness count from?
 *
 * The last activity of any kind. The `||` fallback covers a cached lead
 * fetched before the field existed, which is the pre-MKS-294 answer.
 */
export function leadActivityAt(lead: LeadActivityFields): string {
  return lead.lastActivityAt || lead.stageChangedAt;
}

/** Whole days between `iso` and `now`; null for missing/unparseable input. Never throws. */
export function daysSinceActivity(
  iso: string | null | undefined,
  now: number = Date.now()
): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 86_400_000));
}

/**
 * Short, card-sized "how long ago" label: `today`, `1d ago`, `21d ago`.
 * Matches the `Nd ago` shape the task cards already use (MtRotMeter,
 * CommentBubble). Returns an empty string when the timestamp is unusable, so
 * a caller can simply skip rendering.
 */
export function leadActivityAgo(iso: string | null | undefined, now?: number): string {
  const days = daysSinceActivity(iso, now);
  if (days === null) return '';
  if (days === 0) return 'today';
  return `${days}d ago`;
}
